import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import classNames from 'classnames';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Modal } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import DropzoneField from 'components/DropzoneField';
import ConditionalField from 'components/ConditionalField';
import InputField from 'components/InputField';
import RadioGroupField from 'components/RadioGroupField';
import RadioButtonLabel from 'components/RadioButtonLabel';
import { AdditionalDocumentSource } from 'enums';

import './AdditionalDocumentModal.styles.scss';
import {
  composeValidators,
  required,
  websiteWithPathValidation,
} from 'validations';
import { useTranslation } from 'react-i18next';

export type AdditionalDocumentFormValues = {
  source: AdditionalDocumentSource;
  file?: File[];
  name?: string;
  link?: string;
};

type AdditionalDocumentModalProps = {
  className?: string;
  handleSubmit: (values: AdditionalDocumentFormValues) => void;
  title: string;
  description: string;
  buttonText: string;
};

const AdditionalDocumentModal: React.ForwardRefRenderFunction<
  ModalRef,
  AdditionalDocumentModalProps
> = (props, ref) => {
  const { className, handleSubmit, title, description, buttonText } = props;

  const { t } = useTranslation();

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const baseClass = 'additional-document-modal';
  const classes = classNames(baseClass, className);

  const sourceOptions = useMemo(
    () => [
      {
        label: <RadioButtonLabel label={t('AdditionalDocumentModal.upload')} />,
        value: AdditionalDocumentSource.UPLOAD,
      },
      {
        label: <RadioButtonLabel label={t('AdditionalDocumentModal.link')} />,
        value: AdditionalDocumentSource.LINK,
      },
    ],
    [t],
  );

  return (
    <Modal ref={modalRef} className={classes}>
      <Form<AdditionalDocumentFormValues>
        onSubmit={handleSubmit}
        initialValues={{ source: AdditionalDocumentSource.UPLOAD }}
        render={({ handleSubmit, submitting, invalid, values: { source } }) => (
          <form onSubmit={handleSubmit} className={`${baseClass}__form`}>
            <p className={`${baseClass}__form__title`}>{title}</p>
            <p className={`${baseClass}__form__description`}>{description}</p>
            <Field
              name="source"
              component={RadioGroupField}
              options={sourceOptions}
              wrapperClassName={`${baseClass}__form__source`}
            />
            {source === AdditionalDocumentSource.LINK ? (
              <div className={`${baseClass}__form__link-fields`}>
                <ConditionalField<string>
                  name="name"
                  component={InputField}
                  label={t('AdditionalDocumentModal.nameLabel')}
                  validate={required()}
                  maxLength={100}
                  required
                />
                <ConditionalField<string>
                  name="link"
                  component={InputField}
                  label={t('AdditionalDocumentModal.linkLabel')}
                  placeholder={t('AdditionalDocumentModal.linkPlaceholder')}
                  validate={composeValidators(
                    required(),
                    websiteWithPathValidation(),
                  )}
                  required
                />
              </div>
            ) : (
              <ConditionalField<File[]>
                name="file"
                component={DropzoneField}
                accept={{
                  'image/jpeg': [],
                  'image/png': [],
                  'application/pdf': [],
                }}
                uploadFileText={t('uploadAPdfPngJpgFile')}
                validate={required()}
              />
            )}
            <Button type="submit" disabled={invalid || submitting}>
              {buttonText}
            </Button>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(AdditionalDocumentModal);
